import { ExpertProfileCreateDto, ExpertProfileType } from '@@types/api.types.ts';
import { ErrorMessage } from '@components/shared/error-message.component';
import { FormField } from '@components/shared/form-field.component';
import { FormLabel } from '@components/shared/form-label.component';
import { MultiSelect } from '@components/ui/multi-select';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { useGetCapabilities } from '@queries/capability.query.ts';
import { useGetCategories } from '@queries/category.query.ts';
import { useGetEffects } from '@queries/effect.query.ts';
import { useGetTechnologyDomains } from '@queries/technology.query.ts';
import { PHASES_OF_OPERATIONS_LABELS } from '@utils/constants';
import { mapToSelectOptions } from '@utils/select.util';
import { FunctionComponent } from 'react';
import { ExpertProfileComponentProps } from './expert-profile-modal.component';

interface RangeOption {
  value: string;
  label: string;
}

const phaseOptions = Array.from({ length: 6 }, (_, i) => {
  return {
    value: i.toString(),
    label: `${i} - ${PHASES_OF_OPERATIONS_LABELS[i]}`,
  };
});

const generatePhaseOptions = (selectedMin?: number, selectedMax?: number, isMaxOptions = false): RangeOption[] => {
  return phaseOptions.filter((option) => {
    const value = parseInt(option.value, 10);
    if (isMaxOptions && selectedMin !== undefined && value < selectedMin) {
      return false;
    }
    if (!isMaxOptions && selectedMax !== undefined && value > selectedMax) {
      return false;
    }
    return true;
  });
};

const generateRangeOptions = (
  start: number,
  end: number,
  selectedMin?: number,
  selectedMax?: number,
  isMaxOptions = false,
): RangeOption[] => {
  const options: RangeOption[] = [];
  for (let i = start; i <= end; i++) {
    // For max options, only show numbers greater than or equal to the selected min
    if (isMaxOptions && selectedMin !== undefined && i < selectedMin) {
      continue;
    }
    // For min options, only show numbers less than or equal to the selected max
    if (!isMaxOptions && selectedMax !== undefined && i > selectedMax) {
      continue;
    }
    options.push({
      value: i.toString(),
      label: i.toString(),
    });
  }
  return options;
};

export const ExpertProfileModalExpertiseStep: FunctionComponent<ExpertProfileComponentProps> = (props) => {
  const { formData, setFormData, errors } = props;

  const { data: categoriesData, isLoading: isLoadingCategories, error: categoriesError } = useGetCategories();
  const {
    data: technologyDomainsData,
    isLoading: isLoadingTechnologyDomains,
    error: technologyDomainsError,
  } = useGetTechnologyDomains();
  const { data: capabilitiesData, isLoading: isLoadingCapabilities, error: capabilitiesError } = useGetCapabilities();
  const { data: effectsData, isLoading: isLoadingEffects, error: effectsError } = useGetEffects();

  const technologyCategoryOptions = mapToSelectOptions(categoriesData);
  const technologyDomainOptions = mapToSelectOptions(technologyDomainsData);
  const capabilityOptions = mapToSelectOptions(capabilitiesData);
  const effectOptions = mapToSelectOptions(effectsData);

  const handleRangeChange = (valueString: string, field: keyof ExpertProfileCreateDto, index: 0 | 1) => {
    const value = parseInt(valueString, 10);
    const currentRange = (formData[field] as number[] | undefined) || [undefined, undefined];
    const newRange: (number | undefined)[] = [...currentRange];
    newRange[index] = isNaN(value) ? undefined : value;

    const finalRange = newRange.every((val) => val === undefined) ? undefined : newRange;

    setFormData((prev) => ({
      ...prev,
      [field]: finalRange,
    }));
  };

  const handleMultiSelectChange = (name: keyof ExpertProfileCreateDto, selectedValues: string[]) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedValues.length > 0 ? selectedValues : undefined,
    }));
  };

  const getRangeOptions = (
    field: 'trlAssessmentRange' | 'mrlAssessmentRange' | 'phases',
    isMaxOptions = false,
  ): RangeOption[] => {
    const range = formData[field];
    const [selectedMin, selectedMax] = range || [undefined, undefined];

    if (field === 'phases') {
      return generatePhaseOptions(selectedMin, selectedMax, isMaxOptions);
    }

    return generateRangeOptions(1, 9, selectedMin, selectedMax, isMaxOptions);
  };

  return (
    <div className="space-y-6">
      <FormField>
        <FormLabel label="TRL Assessment Range" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
          <div>
            <Select
              value={formData.trlAssessmentRange?.[0]?.toString() ?? ''}
              onValueChange={(val) => handleRangeChange(val, 'trlAssessmentRange', 0)}
            >
              <SelectTrigger className="bg-gray-100">
                <SelectValue placeholder="Select Min TRL" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {getRangeOptions('trlAssessmentRange').map((opt) => (
                    <SelectItem
                      key={`trl-min-${opt.value}`}
                      value={opt.value}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select
              value={formData.trlAssessmentRange?.[1]?.toString() ?? ''}
              onValueChange={(val) => handleRangeChange(val, 'trlAssessmentRange', 1)}
            >
              <SelectTrigger className="bg-gray-100">
                <SelectValue placeholder="Select Max TRL" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {getRangeOptions('trlAssessmentRange', true).map((opt) => (
                    <SelectItem
                      key={`trl-max-${opt.value}`}
                      value={opt.value}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1 md:col-span-2">
            <ErrorMessage zodError={errors.trlAssessmentRange} />
          </div>
        </div>
      </FormField>

      <FormField>
        <FormLabel label="MRL Assessment Range" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
          <div>
            <Select
              value={formData.mrlAssessmentRange?.[0]?.toString() ?? ''}
              onValueChange={(val) => handleRangeChange(val, 'mrlAssessmentRange', 0)}
            >
              <SelectTrigger className="bg-gray-100">
                <SelectValue placeholder="Select Min MRL" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {getRangeOptions('mrlAssessmentRange').map((opt) => (
                    <SelectItem
                      key={`mrl-min-${opt.value}`}
                      value={opt.value}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select
              value={formData.mrlAssessmentRange?.[1]?.toString() ?? ''}
              onValueChange={(val) => handleRangeChange(val, 'mrlAssessmentRange', 1)}
            >
              <SelectTrigger className="bg-gray-100">
                <SelectValue placeholder="Select Max MRL" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {getRangeOptions('mrlAssessmentRange', true).map((opt) => (
                    <SelectItem
                      key={`mrl-max-${opt.value}`}
                      value={opt.value}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1 md:col-span-2">
            <ErrorMessage zodError={errors.mrlAssessmentRange} />
          </div>
        </div>
      </FormField>

      <FormField>
        <FormLabel label="Technology Categories" />
        <MultiSelect
          options={technologyCategoryOptions}
          onValueChange={(selected) => handleMultiSelectChange('categories', selected)}
          value={formData.categories || []}
          placeholder="Select technology categories"
          disabled={isLoadingCategories}
          className="bg-gray-100"
        />
        <ErrorMessage
          zodError={errors.categories}
          message={categoriesError?.message}
        />
      </FormField>

      <FormField>
        <FormLabel label="Operating Environments" />
        <MultiSelect
          options={technologyDomainOptions}
          onValueChange={(selected) => handleMultiSelectChange('domains', selected)}
          value={formData.domains || []}
          placeholder="Select domains"
          disabled={isLoadingTechnologyDomains}
          className="bg-gray-100"
        />
        <ErrorMessage
          zodError={errors.domains}
          message={technologyDomainsError?.message}
        />
      </FormField>

      {formData.type === ExpertProfileType.VETERAN && (
        <>
          <FormField>
            <FormLabel label="Joint Capability Areas" />
            <MultiSelect
              options={capabilityOptions}
              onValueChange={(selected) => handleMultiSelectChange('capabilities', selected)}
              value={formData.capabilities || []}
              placeholder="Select JCAs"
              disabled={isLoadingCapabilities}
              className="bg-gray-100"
            />
            <ErrorMessage
              zodError={errors.capabilities}
              message={capabilitiesError?.message}
            />
          </FormField>
          <FormField>
            <FormLabel label="Effects Expertise" />
            <MultiSelect
              options={effectOptions}
              onValueChange={(selected) => handleMultiSelectChange('effects', selected)}
              value={formData.effects || []}
              placeholder="Select effects"
              disabled={isLoadingEffects}
              className="bg-gray-100"
            />
            <ErrorMessage
              zodError={errors.effects}
              message={effectsError?.message}
            />
          </FormField>

          <FormField>
            <FormLabel label="Phases of Operation" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
              <div className="md:col-span-1">
                <Select
                  value={formData.phases?.[0]?.toString() ?? ''}
                  onValueChange={(val) => handleRangeChange(val, 'phases', 0)}
                >
                  <SelectTrigger className="bg-gray-100">
                    <SelectValue placeholder="Select Phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {getRangeOptions('phases').map((opt) => (
                        <SelectItem
                          key={`phase-min-${opt.value}`}
                          value={opt.value}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1">
                <Select
                  value={formData.phases?.[1]?.toString() ?? ''}
                  onValueChange={(val) => handleRangeChange(val, 'phases', 1)}
                >
                  <SelectTrigger className="bg-gray-100">
                    <SelectValue placeholder="Select Phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {getRangeOptions('phases', true).map((opt) => (
                        <SelectItem
                          key={`phase-max-${opt.value}`}
                          value={opt.value}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1 md:col-span-2">
                <ErrorMessage zodError={errors.phases} />
              </div>
            </div>
          </FormField>
        </>
      )}
    </div>
  );
};
